"use client";

import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Scale, Loader2, Calendar, Wallet, TrendingDown } from "lucide-react";
import { API_ENDPOINTS } from "../../constants/config";
import { toast } from "react-hot-toast";

export default function StaffLeaveBalance() {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [balance, setBalance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [staffId, setStaffId] = useState(null);

  useEffect(() => {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        const id = user._id || user.id;
        if (id) setStaffId(id);
      } catch (e) {
        toast.error("Could not load profile");
      }
    }
  }, []);

  useEffect(() => {
    if (!staffId) return;
    const load = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const url = API_ENDPOINTS.leaveBalance(`year=${year}&staffId=${staffId}`);
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        if (data.success) setBalance(data.data || []);
        else setBalance([]);
      } catch (e) {
        toast.error("Failed to load leave balance");
        setBalance([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [year, staffId]);

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
  const myBalance = balance[0] || null;
  const rows = myBalance?.byLeaveType || [];

  const summary = myBalance
    ? myBalance.byLeaveType.reduce(
        (acc, lt) => {
          const payType = lt.payType || "unpaid";
          acc[payType].used += lt.usedDays ?? 0;
          acc[payType].balance += lt.balance ?? 0;
          acc.totalUsed += lt.usedDays ?? 0;
          acc.totalBalance += lt.balance ?? 0;
          return acc;
        },
        { paid: { used: 0, balance: 0 }, unpaid: { used: 0, balance: 0 }, totalUsed: 0, totalBalance: 0 }
      )
    : null;

  if (!staffId) {
    return (
      <div className="flex justify-center py-12 text-slate-500">
        Loading your profile…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-lg rounded-xl overflow-hidden">
        <CardHeader className="bg-slate-50 border-b border-slate-200/80">
          <CardTitle className="flex items-center gap-2 text-slate-800 text-xl font-semibold">
            <Scale className="h-5 w-5 text-[#0263cc]" />
            My Leave Balance
          </CardTitle>
          <p className="text-sm text-slate-500 mt-1 mb-4">
            Your leave for the year: used days, balance remaining, and paid vs unpaid.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-600 flex items-center gap-1">
                <Calendar className="h-3.5 w-3" /> Year
              </label>
              <Select value={year} onValueChange={setYear}>
                <SelectTrigger className="w-[100px] bg-white">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  {years.map((y) => (
                    <SelectItem key={y} value={String(y)}>
                      {y}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>

        {loading && (
          <div className="flex justify-center items-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-[#0263cc]" />
          </div>
        )}

        {!loading && summary && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-6 bg-white">
              <Card className="overflow-hidden border-blue-100 bg-blue-50/30">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-lg bg-blue-100">
                      <Wallet className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-slate-600 uppercase tracking-wide">Paid Leave</p>
                      <div className="flex items-baseline gap-2 mt-1">
                        <span className="text-2xl font-bold text-slate-900">{summary.paid.used.toFixed(1)}</span>
                        <span className="text-sm text-slate-500">used / {summary.paid.balance.toFixed(1)} left</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="overflow-hidden border-orange-100 bg-orange-50/30">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-lg bg-orange-100">
                      <TrendingDown className="h-5 w-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-slate-600 uppercase tracking-wide">Unpaid Leave</p>
                      <div className="flex items-baseline gap-2 mt-1">
                        <span className="text-2xl font-bold text-slate-900">{summary.unpaid.used.toFixed(1)}</span>
                        <span className="text-sm text-slate-500">used</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="overflow-hidden border-slate-200 bg-slate-50/50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-lg bg-slate-200">
                      <Calendar className="h-5 w-5 text-slate-700" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-slate-600 uppercase tracking-wide">Total Usage</p>
                      <div className="flex items-baseline gap-2 mt-1">
                        <span className="text-2xl font-bold text-slate-900">{summary.totalUsed.toFixed(1)}</span>
                        <span className="text-sm text-slate-500">days</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="px-6 pb-6">
              <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 hover:bg-slate-50">
                      <TableHead className="font-semibold text-slate-700">Leave Type</TableHead>
                      <TableHead className="font-semibold text-slate-700">Pay Type</TableHead>
                      <TableHead className="font-semibold text-slate-700 text-right">Allocated</TableHead>
                      <TableHead className="font-semibold text-slate-700 text-right">Used</TableHead>
                      <TableHead className="font-semibold text-slate-700 text-right">Balance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                          No leave types configured yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      rows.map((lt, idx) => (
                        <TableRow key={idx} className="hover:bg-slate-50/50">
                          <TableCell className="font-medium text-slate-800">{lt.name}</TableCell>
                          <TableCell>
                            <Badge
                              variant={lt.payType === "paid" ? "default" : "secondary"}
                              className={
                                lt.payType === "paid"
                                  ? "bg-blue-100 text-blue-700 hover:bg-blue-200"
                                  : "bg-orange-100 text-orange-700 hover:bg-orange-200"
                              }
                            >
                              {lt.payType || "unpaid"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right text-slate-700">{lt.allocated ?? "—"}</TableCell>
                          <TableCell className="text-right text-slate-700 font-medium">{lt.usedDays?.toFixed(1) ?? 0}</TableCell>
                          <TableCell className="text-right">
                            <span className={`font-semibold ${lt.balance < 0 ? "text-red-600" : "text-green-600"}`}>
                              {lt.balance?.toFixed(1) ?? 0}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </>
        )}

        {!loading && !summary && (
          <div className="flex flex-col items-center justify-center py-16 text-slate-500">
            <Scale className="h-12 w-12 mb-3 text-slate-300" />
            <p className="text-base font-medium">No leave balance data found.</p>
            <p className="text-sm">Contact your administrator if you think this is an error.</p>
          </div>
        )}
      </Card>
    </div>
  );
}
